import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';

import { extractAccessToken } from 'common/helpers';
import { getUserAccountDataByEmailV2 } from 'data/link/respositories/taskRepository';

import {
    ContactOption,
    CreateTicketRequest,
    HasteOption,
    SaveTicketDraftRequest,
    SelectOption,
    SubWorkGroupOption,
    TicketCreationOptions,
    TicketDraft,
    TicketDraftSummary,
    TicketFormState,
    WorkGroupOption
} from 'data/ticketCreation/schemas';
import {
    createTicket,
    createTicketDraft,
    deleteTicketDraft,
    getAssignmentPreference,
    getTicketCreationOptions,
    getTicketDraft,
    getTicketDrafts,
    getWorkGroupUsers,
    updateTicketDraft
} from 'data/ticketCreation/repositories/ticketCreation.repository';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onCreated?: () => void | Promise<void>;
};

function atTime(date: Date, hours: number) {
    const result = new Date(date);
    result.setHours(hours, 0, 0, 0);
    return result;
}

const UNCATEGORIZED_WORK_GROUP_LABEL = 'Uncategorized Work Group';

function isUncategorizedWorkGroup(workGroup: WorkGroupOption) {
    const name = workGroup.workgroup_name.trim().toLowerCase();
    return name.includes('ไม่ระบุโครงการ') || name.includes('not set project');
}

function getUncategorizedWorkGroupCode(options?: TicketCreationOptions) {
    return (
        options?.task_builder_information.workgroup_list.find(isUncategorizedWorkGroup)
            ?.workgroup_code ?? ''
    );
}

function createEmptyForm(taskOwnerId = '', workGroupCode = ''): TicketFormState {
    const today = new Date();
    return {
        taskOwnerId,
        workGroupCode,
        subWorkGroupCode: '',
        firstAssignId: '',
        participantIds: [],
        hasteCode: '',
        subject: '',
        detail: '',
        startAt: atTime(today, 9),
        endAt: atTime(today, 18),
        sendEmail: true
    };
}

function formatLocalDateTime(date: Date) {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
        date.getHours()
    )}:${pad(date.getMinutes())}:00`;
}

function serializeForm(form: TicketFormState) {
    return JSON.stringify({
        ...form,
        startAt: formatLocalDateTime(form.startAt),
        endAt: formatLocalDateTime(form.endAt)
    });
}

function contactName(contact: ContactOption, fallbackToLinkId = true) {
    const thaiName = [contact.first_name_th, contact.last_name_th].filter(Boolean).join(' ').trim();
    if (thaiName) return thaiName;

    const englishName = [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim();
    if (englishName) return englishName;

    return contact.link_name || contact.email || (fallbackToLinkId ? contact.link_id : '');
}

function scopedByWorkGroup<T extends { workgroup_code: string }>(
    items: T[],
    workGroupCode: string,
    getCode: (item: T) => string
) {
    if (!workGroupCode) return [];

    const result = new Map<string, T>();
    items
        .filter(item => item.workgroup_code === '*')
        .forEach(item => result.set(getCode(item), item));
    items
        .filter(item => item.workgroup_code === workGroupCode)
        .forEach(item => result.set(getCode(item), item));

    return Array.from(result.values());
}

function draftToForm(draft: TicketDraft): TicketFormState {
    return {
        taskOwnerId: draft.task_owner_id,
        workGroupCode: draft.work_group_code ?? '',
        subWorkGroupCode: draft.sub_work_group_code ?? '',
        firstAssignId: draft.first_assign_id ?? '',
        participantIds: draft.participant_ids ?? [],
        hasteCode: draft.haste_code ?? '',
        subject: draft.subject ?? '',
        detail: draft.detail ?? '',
        startAt: draft.start_at ? new Date(draft.start_at) : atTime(new Date(), 9),
        endAt: draft.end_at ? new Date(draft.end_at) : atTime(new Date(), 18),
        sendEmail: true
    };
}

function toDraftPayload(form: TicketFormState, versionNo?: number): SaveTicketDraftRequest {
    return {
        version_no: versionNo,
        task_owner_id: form.taskOwnerId,
        work_group_code: form.workGroupCode || undefined,
        sub_work_group_code: form.subWorkGroupCode || undefined,
        first_assign_id: form.firstAssignId || undefined,
        participant_ids: form.participantIds,
        haste_code: form.hasteCode || undefined,
        subject: form.subject.trim() || undefined,
        detail: form.detail.trim() || undefined,
        start_at: formatLocalDateTime(form.startAt),
        end_at: formatLocalDateTime(form.endAt)
    };
}

function toCreatePayload(form: TicketFormState, draftId?: string): CreateTicketRequest {
    return {
        draft_id: draftId,
        task_owner_id: form.taskOwnerId,
        work_group_id: form.workGroupCode,
        sub_work_group_id: form.subWorkGroupCode || undefined,
        first_assign_id: form.firstAssignId,
        participant_ids: Array.from(new Set(form.participantIds)),
        haste_code: form.hasteCode || undefined,
        subject: form.subject.trim(),
        detail: form.detail.trim() || undefined,
        start_at: formatLocalDateTime(form.startAt),
        end_at: formatLocalDateTime(form.endAt),
        send_email: form.sendEmail
    };
}

function validateCreateForm(form: TicketFormState) {
    if (!form.taskOwnerId) return 'ไม่พบ Owner ของงาน';
    if (!form.workGroupCode) return 'กรุณาเลือก Work Group';
    if (!form.firstAssignId) return 'กรุณาเลือกผู้รับมอบหมาย';
    if (!form.subject.trim()) return 'กรุณาระบุหัวข้องาน';
    if (Number.isNaN(form.startAt.getTime()) || Number.isNaN(form.endAt.getTime())) {
        return 'กรุณาระบุวันและเวลาให้ถูกต้อง';
    }
    if (form.endAt < form.startAt) return 'วันสิ้นสุดต้องไม่น้อยกว่าวันเริ่ม';

    return '';
}

function attachmentIdentity(file: File) {
    return `${file.name}\u0000${file.size}\u0000${file.lastModified}`;
}

function useCreateTicketViewModel({ isOpen, onClose, onCreated }: Props) {
    const [form, setForm] = useState<TicketFormState>(() => createEmptyForm());
    const [attachments, setAttachments] = useState<File[]>([]);
    const [attachmentWarning, setAttachmentWarning] = useState('');
    const formRef = useRef(form);
    const [baseline, setBaseline] = useState(() => serializeForm(createEmptyForm()));
    const baselineRef = useRef(baseline);
    const [options, setOptions] = useState<TicketCreationOptions>();
    const [drafts, setDrafts] = useState<TicketDraftSummary[]>([]);
    const [selectedDraft, setSelectedDraft] = useState<TicketDraft>();
    const [ownerName, setOwnerName] = useState('');
    const [linkId, setLinkId] = useState('');
    const [workGroupUsers, setWorkGroupUsers] = useState<ContactOption[]>([]);
    const [loadedWorkGroupCode, setLoadedWorkGroupCode] = useState('');
    const [optionsError, setOptionsError] = useState('');
    const [workGroupUsersError, setWorkGroupUsersError] = useState('');
    const [assignmentPreferenceWarning, setAssignmentPreferenceWarning] = useState('');
    const [draftsError, setDraftsError] = useState('');
    const [draftWarning, setDraftWarning] = useState('');
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const [isLoadingWorkGroupUsers, setIsLoadingWorkGroupUsers] = useState(false);
    const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
    const [isLoadingDraft, setIsLoadingDraft] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const workGroupUsersAbortController = useRef<AbortController>();
    const applyAssignmentPreferenceRef = useRef(true);

    useEffect(() => {
        formRef.current = form;
    }, [form]);

    useEffect(() => {
        baselineRef.current = baseline;
    }, [baseline]);

    const isDirty = useMemo(
        () => serializeForm(form) !== baseline || attachments.length > 0,
        [attachments.length, baseline, form]
    );

    const workGroupOptions = useMemo<SelectOption[]>(
        () =>
            (options?.task_builder_information.workgroup_list ?? []).map(item => ({
                value: item.workgroup_code,
                label: isUncategorizedWorkGroup(item)
                    ? UNCATEGORIZED_WORK_GROUP_LABEL
                    : item.workgroup_name
            })),
        [options]
    );

    const uncategorizedWorkGroupCode = useMemo(
        () => getUncategorizedWorkGroupCode(options),
        [options]
    );

    const scopedSubWorkGroups = useMemo(
        () =>
            scopedByWorkGroup<SubWorkGroupOption>(
                options?.task_builder_information.sub_workgroup_list ?? [],
                form.workGroupCode,
                item => item.sub_workgroup_code
            ),
        [form.workGroupCode, options]
    );

    const subWorkGroupOptions = useMemo<SelectOption[]>(
        () =>
            scopedSubWorkGroups.map(item => ({
                value: item.sub_workgroup_code,
                label: item.sub_workgroup_name
            })),
        [scopedSubWorkGroups]
    );

    const hasteOptions = useMemo(
        () =>
            scopedByWorkGroup<HasteOption>(
                options?.task_builder_information.haste_list ?? [],
                form.workGroupCode || '*',
                item => item.haste_code
            ),
        [form.workGroupCode, options]
    );

    const assigneeOptions = useMemo<SelectOption[]>(() => {
        const uniqueContacts = new Map<string, SelectOption>();
        workGroupUsers.forEach(contact => {
            if (!contact.link_id) return;
            uniqueContacts.set(contact.link_id, {
                value: contact.link_id,
                label: contactName(contact)
            });
        });

        return Array.from(uniqueContacts.values()).sort((left, right) =>
            left.label.localeCompare(right.label, ['th', 'en'], {
                sensitivity: 'base',
                numeric: true
            })
        );
    }, [workGroupUsers]);

    const workGroupUserIds = useMemo(
        () => new Set(workGroupUsers.map(contact => contact.link_id).filter(Boolean)),
        [workGroupUsers]
    );
    const hasValidAssignments =
        loadedWorkGroupCode === form.workGroupCode &&
        workGroupUserIds.has(form.firstAssignId) &&
        form.participantIds.every(participantId => workGroupUserIds.has(participantId));

    const dateError = form.endAt < form.startAt ? 'วันสิ้นสุดต้องไม่น้อยกว่าวันเริ่ม' : '';
    const canSubmit =
        !isLoadingOptions &&
        !isLoadingWorkGroupUsers &&
        !isSaving &&
        !isDeleting &&
        !isSubmitting &&
        !workGroupUsersError &&
        hasValidAssignments &&
        !validateCreateForm(form);

    const updateField = useCallback(
        <K extends keyof TicketFormState>(key: K, value: TicketFormState[K]) => {
            setForm(current => ({ ...current, [key]: value }));
        },
        []
    );

    const loadDrafts = useCallback(async () => {
        setIsLoadingDrafts(true);
        setDraftsError('');
        try {
            setDrafts(await getTicketDrafts());
        } catch (error) {
            setDraftsError((error as Error).message);
        } finally {
            setIsLoadingDrafts(false);
        }
    }, []);

    const loadOptions = useCallback(async (currentLinkId: string) => {
        setIsLoadingOptions(true);
        setOptionsError('');
        try {
            const nextOptions = await getTicketCreationOptions(currentLinkId);
            setOptions(nextOptions);

            const ownerContact = nextOptions.contacts.result_list.find(
                contact => contact.link_id === currentLinkId
            );
            const currentOwnerName = ownerContact ? contactName(ownerContact, false) : '';
            if (currentOwnerName) setOwnerName(currentOwnerName);
            return nextOptions;
        } catch (error) {
            setOptionsError((error as Error).message);
            return undefined;
        } finally {
            setIsLoadingOptions(false);
        }
    }, []);

    const loadWorkGroupAssignmentOptions = useCallback(
        async (
            currentLinkId: string,
            workGroupCode: string,
            applyPreference: boolean
        ) => {
            workGroupUsersAbortController.current?.abort();

            if (!currentLinkId || !workGroupCode) {
                setWorkGroupUsers([]);
                setLoadedWorkGroupCode('');
                setWorkGroupUsersError('');
                setAssignmentPreferenceWarning('');
                setIsLoadingWorkGroupUsers(false);
                return;
            }

            const abortController = new AbortController();
            workGroupUsersAbortController.current = abortController;
            setWorkGroupUsers([]);
            setLoadedWorkGroupCode('');
            setWorkGroupUsersError('');
            setAssignmentPreferenceWarning('');
            setIsLoadingWorkGroupUsers(true);

            try {
                const usersPromise = getWorkGroupUsers(
                    currentLinkId,
                    workGroupCode,
                    abortController.signal
                );
                const preferencePromise = applyPreference
                    ? getAssignmentPreference(workGroupCode, abortController.signal)
                          .then(preference => ({ preference, error: '' }))
                          .catch(error => ({
                              preference: undefined,
                              error:
                                  axios.isCancel(error) || abortController.signal.aborted
                                      ? ''
                                      : (error as Error).message
                          }))
                    : Promise.resolve({ preference: undefined, error: '' });

                const [response, preferenceResult] = await Promise.all([
                    usersPromise,
                    preferencePromise
                ]);
                if (abortController.signal.aborted) return;

                const users = response.result_list ?? [];
                const validIds = new Set(users.map(user => user.link_id).filter(Boolean));
                setWorkGroupUsers(users);
                setLoadedWorkGroupCode(workGroupCode);
                setAssignmentPreferenceWarning(preferenceResult.error);
                const currentForm = formRef.current;
                if (currentForm.workGroupCode !== workGroupCode) return;

                if (applyPreference && preferenceResult.preference) {
                    const preference = preferenceResult.preference;
                    const nextForm = {
                        ...currentForm,
                        firstAssignId:
                            preference.first_assign_id &&
                            validIds.has(preference.first_assign_id)
                                ? preference.first_assign_id
                                : '',
                        participantIds: Array.from(
                            new Set(
                                (preference.participant_ids ?? []).filter(id =>
                                    validIds.has(id)
                                )
                            )
                        )
                    };
                    setForm(nextForm);
                    if (serializeForm(currentForm) === baselineRef.current) {
                        setBaseline(serializeForm(nextForm));
                    }
                    return;
                }

                if (!applyPreference) {
                    const removedAssignee =
                        !!currentForm.firstAssignId && !validIds.has(currentForm.firstAssignId);
                    const participantIds = currentForm.participantIds.filter(id =>
                        validIds.has(id)
                    );
                    const removedParticipants =
                        participantIds.length !== currentForm.participantIds.length;
                    setForm({
                        ...currentForm,
                        firstAssignId: removedAssignee ? '' : currentForm.firstAssignId,
                        participantIds
                    });

                    if (!removedAssignee && !removedParticipants) return;
                    const warning = [
                        removedAssignee ? 'ผู้รับมอบหมายเดิมไม่ได้อยู่ใน Work Group นี้' : '',
                        removedParticipants ? 'ผู้เกี่ยวข้องบางรายไม่ได้อยู่ใน Work Group นี้' : ''
                    ]
                        .filter(Boolean)
                        .join(' • ');
                    setDraftWarning(currentWarning =>
                        currentWarning ? `${currentWarning} • ${warning}` : warning
                    );
                }
            } catch (error) {
                if (axios.isCancel(error) || abortController.signal.aborted) return;
                setWorkGroupUsers([]);
                setLoadedWorkGroupCode('');
                setWorkGroupUsersError((error as Error).message);
            } finally {
                if (workGroupUsersAbortController.current === abortController) {
                    setIsLoadingWorkGroupUsers(false);
                }
            }
        },
        []
    );

    useEffect(() => {
        if (!isOpen) return;

        const initialize = async () => {
            workGroupUsersAbortController.current?.abort();
            applyAssignmentPreferenceRef.current = true;
            setOptions(undefined);
            setAttachments([]);
            setAttachmentWarning('');
            setSelectedDraft(undefined);
            setWorkGroupUsers([]);
            setLoadedWorkGroupCode('');
            setDraftWarning('');
            setOptionsError('');
            setWorkGroupUsersError('');
            setAssignmentPreferenceWarning('');
            setDraftsError('');
            setIsLoadingOptions(true);
            setIsLoadingWorkGroupUsers(false);

            const draftPromise = loadDrafts();

            try {
                const email = extractAccessToken().email;
                if (!email) throw new Error('ไม่พบ Email ของผู้ใช้งาน');

                const [error, accountLinkId] = await getUserAccountDataByEmailV2(email);
                if (error) throw new Error(error.message);

                const effectiveLinkId = accountLinkId?.trim();
                if (!effectiveLinkId) throw new Error('ไม่พบ Link ID ของผู้ใช้งาน');

                setLinkId(effectiveLinkId);
                setOwnerName(email);

                const nextOptions = await loadOptions(effectiveLinkId);
                const emptyForm = createEmptyForm(
                    effectiveLinkId,
                    getUncategorizedWorkGroupCode(nextOptions)
                );
                setForm(emptyForm);
                setBaseline(serializeForm(emptyForm));
            } catch (error) {
                const emptyForm = createEmptyForm();
                setLinkId('');
                setOwnerName('');
                setForm(emptyForm);
                setBaseline(serializeForm(emptyForm));
                setOptionsError((error as Error).message);
                setIsLoadingOptions(false);
            }

            await draftPromise;
        };

        void initialize();
    }, [isOpen, loadDrafts, loadOptions]);

    useEffect(() => {
        if (!isOpen) {
            workGroupUsersAbortController.current?.abort();
            return;
        }

        void loadWorkGroupAssignmentOptions(
            linkId,
            form.workGroupCode,
            applyAssignmentPreferenceRef.current
        );
        return () => workGroupUsersAbortController.current?.abort();
    }, [
        form.workGroupCode,
        isOpen,
        linkId,
        loadWorkGroupAssignmentOptions,
        selectedDraft?.draft_id
    ]);

    const confirmDiscardChanges = useCallback(async () => {
        if (!isDirty) return true;

        const result = await Swal.fire({
            title: 'ยังไม่ได้บันทึกการเปลี่ยนแปลง',
            text: 'ต้องการทิ้งข้อมูลที่แก้ไขหรือไม่',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'ทิ้งข้อมูล',
            cancelButtonText: 'กลับไปบันทึก'
        });
        return result.isConfirmed;
    }, [isDirty]);

    const handleRequestClose = useCallback(async () => {
        if (isSubmitting) return;
        if (await confirmDiscardChanges()) onClose();
    }, [confirmDiscardChanges, isSubmitting, onClose]);

    const handleNewDraft = useCallback(async () => {
        if (!(await confirmDiscardChanges())) return;

        const emptyForm = createEmptyForm(linkId, uncategorizedWorkGroupCode);
        applyAssignmentPreferenceRef.current = true;
        setSelectedDraft(undefined);
        setAttachments([]);
        setAttachmentWarning('');
        setDraftWarning('');
        setAssignmentPreferenceWarning('');
        setForm(emptyForm);
        setBaseline(serializeForm(emptyForm));
    }, [confirmDiscardChanges, linkId, uncategorizedWorkGroupCode]);

    const sanitizeDraft = useCallback(
        (draft: TicketDraft) => {
            const nextForm = draftToForm(draft);
            const warnings: string[] = [];
            const builder = options?.task_builder_information;

            if (linkId && nextForm.taskOwnerId !== linkId) {
                warnings.push('Owner ของงานร่างถูกเปลี่ยนเป็นผู้ใช้งานปัจจุบัน');
                nextForm.taskOwnerId = linkId;
            }

            if (
                builder &&
                nextForm.workGroupCode &&
                !builder.workgroup_list.some(item => item.workgroup_code === nextForm.workGroupCode)
            ) {
                warnings.push('Work Group เดิมไม่มีอยู่ในข้อมูลล่าสุด');
                nextForm.workGroupCode = '';
                nextForm.subWorkGroupCode = '';
                nextForm.hasteCode = '';
            }

            if (builder && nextForm.subWorkGroupCode) {
                const validSubWorkGroups = scopedByWorkGroup(
                    builder.sub_workgroup_list,
                    nextForm.workGroupCode,
                    item => item.sub_workgroup_code
                );
                if (
                    !validSubWorkGroups.some(
                        item => item.sub_workgroup_code === nextForm.subWorkGroupCode
                    )
                ) {
                    warnings.push('Sub Work Group เดิมใช้ไม่ได้กับ Work Group นี้');
                    nextForm.subWorkGroupCode = '';
                }
            }

            if (builder && nextForm.hasteCode) {
                const validHasteOptions = scopedByWorkGroup(
                    builder.haste_list,
                    nextForm.workGroupCode,
                    item => item.haste_code
                );
                if (!validHasteOptions.some(item => item.haste_code === nextForm.hasteCode)) {
                    warnings.push('ค่าความเร่งด่วนเดิมใช้ไม่ได้กับ Work Group นี้');
                    nextForm.hasteCode = '';
                }
            }

            return { nextForm, warning: warnings.join(' • ') };
        },
        [linkId, options]
    );

    const handleSelectDraft = useCallback(
        async (draftId: string) => {
            if (!draftId || draftId === selectedDraft?.draft_id) return;
            if (!(await confirmDiscardChanges())) return;

            setIsLoadingDraft(true);
            try {
                const draft = await getTicketDraft(draftId);
                const { nextForm, warning } = sanitizeDraft(draft);
                applyAssignmentPreferenceRef.current = false;
                setSelectedDraft(draft);
                setAttachments([]);
                setAttachmentWarning('');
                setDraftWarning(warning);
                setAssignmentPreferenceWarning('');
                setForm(nextForm);
                setBaseline(serializeForm(nextForm));
            } catch (error) {
                await Swal.fire('เปิดงานร่างไม่สำเร็จ', (error as Error).message, 'error');
                await loadDrafts();
            } finally {
                setIsLoadingDraft(false);
            }
        },
        [confirmDiscardChanges, loadDrafts, sanitizeDraft, selectedDraft?.draft_id]
    );

    const handleSaveDraft = useCallback(async () => {
        if (!linkId) {
            await Swal.fire('บันทึกไม่ได้', 'ไม่พบ Owner ของงาน', 'error');
            return;
        }

        setIsSaving(true);
        try {
            const formToSave = { ...form, taskOwnerId: linkId };
            const savedDraft = selectedDraft
                ? await updateTicketDraft(
                      selectedDraft.draft_id,
                      toDraftPayload(formToSave, selectedDraft.version_no)
                  )
                : await createTicketDraft(toDraftPayload(formToSave));

            applyAssignmentPreferenceRef.current = false;
            setSelectedDraft(savedDraft);
            setForm(formToSave);
            setBaseline(serializeForm(formToSave));
            setDraftWarning('');
            await loadDrafts();
            await Swal.fire(
                'สำเร็จ',
                attachments.length
                    ? 'บันทึกงานร่างแล้ว โดยไฟล์แนบไม่ได้ถูกบันทึก กรุณาเลือกไฟล์ใหม่ตอนสร้างงาน'
                    : 'บันทึกงานร่างแล้ว',
                attachments.length ? 'warning' : 'success'
            );
            setAttachments([]);
            setAttachmentWarning('');
            onClose();
        } catch (error) {
            await Swal.fire('บันทึกงานร่างไม่สำเร็จ', (error as Error).message, 'error');
        } finally {
            setIsSaving(false);
        }
    }, [attachments.length, form, linkId, loadDrafts, onClose, selectedDraft]);

    const handleDeleteDraft = useCallback(async () => {
        if (!selectedDraft) return;

        const confirmation = await Swal.fire({
            title: 'ลบงานร่างนี้หรือไม่',
            text: selectedDraft.subject || 'ยังไม่ระบุหัวข้อ',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'ลบงานร่าง',
            cancelButtonText: 'ยกเลิก'
        });
        if (!confirmation.isConfirmed) return;

        setIsDeleting(true);
        try {
            await deleteTicketDraft(selectedDraft.draft_id);
            const emptyForm = createEmptyForm(linkId, uncategorizedWorkGroupCode);
            applyAssignmentPreferenceRef.current = true;
            setSelectedDraft(undefined);
            setAttachments([]);
            setAttachmentWarning('');
            setDraftWarning('');
            setAssignmentPreferenceWarning('');
            setForm(emptyForm);
            setBaseline(serializeForm(emptyForm));
            await loadDrafts();
        } catch (error) {
            await Swal.fire('ลบงานร่างไม่สำเร็จ', (error as Error).message, 'error');
        } finally {
            setIsDeleting(false);
        }
    }, [linkId, loadDrafts, selectedDraft, uncategorizedWorkGroupCode]);

    const handleWorkGroupChange = useCallback((workGroupCode: string) => {
        workGroupUsersAbortController.current?.abort();
        applyAssignmentPreferenceRef.current = true;
        setWorkGroupUsers([]);
        setLoadedWorkGroupCode('');
        setWorkGroupUsersError('');
        setAssignmentPreferenceWarning('');
        setDraftWarning('');
        setForm(current => ({
            ...current,
            workGroupCode,
            subWorkGroupCode: '',
            firstAssignId: '',
            participantIds: [],
            hasteCode: ''
        }));
    }, []);

    const handleAddAttachments = useCallback((files: FileList | null) => {
        if (!files?.length) return;

        const selectedFiles = Array.from(files);
        const existingFiles = new Set(attachments.map(attachmentIdentity));
        const filesToAdd: File[] = [];
        let duplicateCount = 0;

        selectedFiles.forEach(file => {
            const identity = attachmentIdentity(file);
            if (existingFiles.has(identity)) {
                duplicateCount += 1;
                return;
            }

            existingFiles.add(identity);
            filesToAdd.push(file);
        });

        if (filesToAdd.length) setAttachments(current => [...current, ...filesToAdd]);
        setAttachmentWarning(
            duplicateCount ? `ข้ามไฟล์ที่เลือกซ้ำ ${duplicateCount} ไฟล์` : ''
        );
    }, [attachments]);

    const handleRemoveAttachment = useCallback((index: number) => {
        setAttachments(current => current.filter((_, currentIndex) => currentIndex !== index));
        setAttachmentWarning('');
    }, []);

    const handleSubmit = useCallback(async () => {
        const validationError = validateCreateForm(form);
        if (validationError) {
            await Swal.fire('ยังสร้าง Ticket ไม่ได้', validationError, 'warning');
            return;
        }

        if (isLoadingWorkGroupUsers || loadedWorkGroupCode !== form.workGroupCode) {
            await Swal.fire(
                'ยังสร้าง Ticket ไม่ได้',
                'กรุณารอโหลดรายชื่อใน Work Group ให้เรียบร้อย',
                'warning'
            );
            return;
        }

        if (workGroupUsersError) {
            await Swal.fire('ยังสร้าง Ticket ไม่ได้', workGroupUsersError, 'warning');
            return;
        }

        if (!workGroupUserIds.has(form.firstAssignId)) {
            await Swal.fire(
                'ยังสร้าง Ticket ไม่ได้',
                'ผู้รับมอบหมายไม่ได้อยู่ใน Work Group ที่เลือก',
                'warning'
            );
            return;
        }

        if (form.participantIds.some(id => !workGroupUserIds.has(id))) {
            await Swal.fire(
                'ยังสร้าง Ticket ไม่ได้',
                'ผู้เกี่ยวข้องบางรายไม่ได้อยู่ใน Work Group ที่เลือก',
                'warning'
            );
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await createTicket(
                toCreatePayload(form, selectedDraft?.draft_id),
                attachments
            );
            if (!result.success) {
                throw new Error('ระบบไม่ยืนยันผลการสร้าง Ticket');
            }

            const draftWasNotUpdated = !!selectedDraft && !result.draft_updated;
            const preferenceWasNotUpdated =
                result.assignment_preference_updated === false;
            const emptyForm = createEmptyForm(linkId, uncategorizedWorkGroupCode);
            applyAssignmentPreferenceRef.current = true;
            setSelectedDraft(undefined);
            setAttachments([]);
            setAttachmentWarning('');
            setDraftWarning('');
            setAssignmentPreferenceWarning('');
            setForm(emptyForm);
            setBaseline(serializeForm(emptyForm));
            await loadDrafts();

            const successWarnings = [
                draftWasNotUpdated
                    ? 'งานร่างยังไม่ถูกปิด กรุณาตรวจสอบรายการงานร่างอีกครั้ง'
                    : '',
                preferenceWasNotUpdated
                    ? 'ไม่สามารถบันทึกผู้รับมอบหมายและผู้เกี่ยวข้องล่าสุดได้'
                    : ''
            ].filter(Boolean);
            await Swal.fire({
                title: 'สร้าง Ticket สำเร็จ',
                text: successWarnings.length
                    ? `Ticket ถูกสร้างแล้ว แต่${successWarnings.join(' และ')}`
                    : 'Ticket ถูกส่งเข้าระบบแล้ว',
                icon: successWarnings.length ? 'warning' : 'success',
                confirmButtonText: 'ตกลง'
            });
            onClose();
            void onCreated?.();
        } catch (error) {
            await Swal.fire('สร้าง Ticket ไม่สำเร็จ', (error as Error).message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    }, [
        form,
        attachments,
        isLoadingWorkGroupUsers,
        linkId,
        loadDrafts,
        loadedWorkGroupCode,
        onClose,
        onCreated,
        selectedDraft,
        uncategorizedWorkGroupCode,
        workGroupUserIds,
        workGroupUsersError
    ]);

    const retryOptions = useCallback(async () => {
        if (!linkId) return;

        const nextOptions = await loadOptions(linkId);
        const defaultWorkGroupCode = getUncategorizedWorkGroupCode(nextOptions);
        if (!defaultWorkGroupCode || form.workGroupCode) return;

        const nextForm = { ...form, workGroupCode: defaultWorkGroupCode };
        setForm(nextForm);
        if (!isDirty) setBaseline(serializeForm(nextForm));
    }, [form, isDirty, linkId, loadOptions]);

    const retryWorkGroupUsers = useCallback(() => {
        void loadWorkGroupAssignmentOptions(
            linkId,
            form.workGroupCode,
            applyAssignmentPreferenceRef.current
        );
    }, [
        form.workGroupCode,
        linkId,
        loadWorkGroupAssignmentOptions,
        selectedDraft
    ]);

    return {
        form,
        attachments,
        attachmentWarning,
        drafts,
        selectedDraft,
        ownerName,
        workGroupOptions,
        subWorkGroupOptions,
        hasteOptions,
        assigneeOptions,
        optionsError,
        workGroupUsersError,
        assignmentPreferenceWarning,
        draftsError,
        draftWarning,
        dateError,
        isDirty,
        isLoadingOptions,
        isLoadingWorkGroupUsers,
        isLoadingDrafts,
        isLoadingDraft,
        isSaving,
        isDeleting,
        isSubmitting,
        canSubmit,
        updateField,
        handleWorkGroupChange,
        handleAddAttachments,
        handleRemoveAttachment,
        handleSelectDraft,
        handleNewDraft,
        handleSaveDraft,
        handleDeleteDraft,
        handleSubmit,
        handleRequestClose,
        retryOptions,
        retryWorkGroupUsers,
        retryDrafts: loadDrafts
    };
}

export default useCreateTicketViewModel;
